/**
 * Every version of one note, newest first (PHASE-0.md §2.5).
 *
 * §2.5 asks that a correction creates a new version **with the old one visible**. Storing
 * the old version does not satisfy that; a screen that shows it does. This is that screen,
 * and it is the only place in phase 0 that renders superseded rows on purpose.
 *
 * **Nothing is filtered, including a retraction.** The patient overview hides
 * `entered-in-error` because a retracted note must not read as current
 * (`DATA-MODEL.md` §5). Here the opposite is true: the question being asked is what the
 * record contains, and a version that is missing from a history is the one thing this view
 * exists to make impossible.
 *
 * **Asked for, not prefetched.** Reading it writes an audit row (AGENTS.md §4), so opening
 * a patient must not fetch thirty histories and claim somebody read thirty notes. The row
 * this writes is truthful: a clinician did ask to see the note's versions.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  fetchNoteVersions,
  NoteHistoryError,
  type NoteHistory,
  type NoteHistoryFailure,
  type NoteVersion,
} from './noteApi'
import {
  noteFieldLabel,
  noteVersionAuthor,
  noteVersionClock,
  noteVersionException,
  noteVersionSignature,
  noteVersionStanding,
  noteVersionWritten,
  offeredNoteTypeLabel,
  type NoteTranslate,
} from './noteDisplay'

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly history: NoteHistory }
  | { readonly kind: 'failed'; readonly failure: NoteHistoryFailure }

function Version({
  version,
  newest,
  t,
}: {
  readonly version: NoteVersion
  readonly newest: number
  readonly t: NoteTranslate
}) {
  const exception = noteVersionException(version, t)
  const clock = noteVersionClock(version, t)

  const sections = (
    [
      ['subjective', version.subjective],
      ['objective', version.objective],
      ['assessment', version.assessment],
      ['plan', version.plan],
      ['bodyText', version.bodyText],
    ] as const
  ).filter(([, text]) => text !== null && text !== '')

  return (
    <li
      className={`note-version${version.version === newest ? ' note-version--current' : ''}`}
    >
      <header className="note-version__header">
        <h4>{t('notes:version.number', { version: version.version })}</h4>

        <p className="note-version__standing">
          {noteVersionStanding(version, newest, t)}
        </p>

        {/* A retraction or an unsigned row, said before the text rather than after it. A
            reader who has already read the note cannot unread it. */}
        {exception !== null && (
          <p className="note-version__exception" role="status">
            {exception}
          </p>
        )}

        <p className="note-version__type">{offeredNoteTypeLabel(version.noteType, t)}</p>
        <p className="note-version__author">{noteVersionAuthor(version, t)}</p>

        {/* Always shown here, unlike the reading view, which shows it only when it differs
            from `effectiveAt`. In a history the time each version was written is the thing
            being looked at. */}
        <p className="note-version__written">{noteVersionWritten(version, t)}</p>

        <p className="note-version__signature">{noteVersionSignature(version, t)}</p>

        {clock !== null && <p className="note-version__clock">{clock}</p>}
      </header>

      {/* The note's own language on the element holding its text — never a reason to
          translate the content (AGENTS.md §3 rule 8). */}
      <div className="note-version__body" lang={version.language}>
        {sections.length === 0 ? (
          <p className="note-version__empty">{t('notes:version.empty')}</p>
        ) : (
          <dl>
            {sections.map(([field, text]) => (
              <div key={field}>
                <dt>{noteFieldLabel(field, t)}</dt>
                <dd>{text}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </li>
  )
}

/**
 * The history of one note.
 *
 * Mounted only once a clinician has asked for it — see the module remarks — so it fetches
 * on mount and the parent controls whether it exists.
 */
export function NoteVersionHistory({
  patientId,
  noteId,
}: {
  readonly patientId: string
  readonly noteId: string
}) {
  const { t } = useTranslation(['common', 'patient', 'notes'])

  const [state, setState] = useState<State>({ kind: 'loading' })

  // Bumped by the retry button, so a retry is a new attempt rather than an imperative
  // refetch to keep in step with the state. `PatientSearchScreen`'s pattern.
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()

    setState({ kind: 'loading' })

    fetchNoteVersions(patientId, noteId, controller.signal).then(
      (history) => {
        if (!controller.signal.aborted) {
          setState({ kind: 'loaded', history })
        }
      },
      (cause: unknown) => {
        if (controller.signal.aborted) {
          return
        }
        setState({
          kind: 'failed',
          failure: cause instanceof NoteHistoryError ? cause.failure : 'unavailable',
        })
      },
    )

    return () => controller.abort()
  }, [patientId, noteId, attempt])

  const newest =
    state.kind === 'loaded'
      ? state.history.versions.reduce((highest, version) => Math.max(highest, version.version), 0)
      : 0

  return (
    <section className="note-history" aria-label={t('notes:history.heading')}>
      <div aria-live="polite">
        {state.kind === 'loading' && (
          <p className="note-history__notice">{t('notes:history.loading')}</p>
        )}

        {state.kind === 'failed' && (
          <div className="note-history__notice note-history__notice--error" role="alert">
            <p>
              {state.failure === 'not-found'
                ? t('notes:history.notFound')
                : t('notes:history.failed')}
            </p>

            {/* No retry for a note that is not there — asking again teaches nothing. */}
            {state.failure === 'unavailable' && (
              <button type="button" onClick={() => setAttempt((value) => value + 1)}>
                {t('notes:history.retry')}
              </button>
            )}
          </div>
        )}

        {state.kind === 'loaded' && (
          <>
            <p className="note-history__count">
              {t('notes:history.count', { count: state.history.versions.length })}
            </p>

            <ol className="note-history__versions">
              {/* Sorted here rather than trusted from the server, for the reason
                  `currentVersion` gives: an order this screen assumed and the server
                  changed would put a superseded version at the top of a history, labelled
                  as current. */}
              {[...state.history.versions]
                .sort((left, right) => right.version - left.version)
                .map((version) => (
                  <Version key={version.versionId} version={version} newest={newest} t={t} />
                ))}
            </ol>
          </>
        )}
      </div>
    </section>
  )
}
